import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { Typography, Chip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ContentCopy, Delete, ArrowForwardIos } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { MdOutlineExtension } from "react-icons/md";

function McpClientNode({ data, isConnectable, id }) {
  const theme = useTheme();
  const storageItems = useNodeStorage();

  const serverName = data?.mcpConfig?.serverName || "Servidor MCP";
  const toolName = data?.mcpConfig?.toolName || "";
  const serverType = data?.mcpConfig?.serverType || "stdio";
  const borderColor = "#8b5cf6"; // Roxo para MCP

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        padding: "12px",
        borderRadius: "8px",
        boxShadow: theme.shadows[3],
        border: `2px solid ${borderColor}`,
        minWidth: "180px",
        maxWidth: "220px",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          top: "50%",
          left: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, ${borderColor} 0%, ${borderColor}dd 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Botões de ação */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: 5,
          top: 5,
          cursor: "pointer",
          gap: 6,
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          style={{
            width: "14px",
            height: "14px",
            color: theme.palette.primary.main,
          }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          style={{
            width: "14px",
            height: "14px",
            color: theme.palette.error.main,
          }}
        />
      </div>

      {/* Header com ícone e nome */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <MdOutlineExtension
          style={{
            width: "22px",
            height: "22px",
            color: borderColor,
          }}
        />
        <Typography
          variant="subtitle2"
          style={{
            fontWeight: "bold",
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {serverName}
        </Typography>
      </div>

      {/* Info da ferramenta */}
      {toolName && (
        <div
          style={{
            backgroundColor:
              theme.palette.type === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.03)",
            padding: "6px 8px",
            borderRadius: "6px",
            marginBottom: "6px",
          }}
        >
          <Typography
            variant="caption"
            style={{
              color: theme.palette.text.secondary,
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Ferramenta: {toolName}
          </Typography>
        </div>
      )}

      {/* Chips de status */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          flexWrap: "wrap",
        }}
      >
        <Chip
          label="MCP"
          size="small"
          style={{
            backgroundColor: borderColor,
            color: "#fff",
            fontSize: "10px",
            height: "20px",
          }}
        />
        <Chip
          label={serverType.toUpperCase()}
          size="small"
          variant="outlined"
          style={{
            fontSize: "10px",
            height: "20px",
            borderColor: borderColor,
            color: borderColor,
          }}
        />
      </div>

      {/* Handle de saída - Sucesso */}
      <Handle
        type="source"
        position={Position.Right}
        id="success"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          top: "35%",
          right: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #22c55e 0%, #16a34a 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Handle de saída - Erro */}
      <Handle
        type="source"
        position={Position.Right}
        id="error"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          top: "65%",
          right: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #ef4444 0%, #dc2626 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>
    </div>
  );
}

export default memo(McpClientNode);
